import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Page,
  PageHeader,
  Select,
  Table,
  type Column,
} from '@evogard/ui';
import { BREAK_MINUTES, breakSlotsOf } from '@evogard/domain';
import type { BreakSettings } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { BREAK_CURRENT_KEY } from '../../components/BreakToggle.js';

const HOURS = Array.from({ length: 25 }, (_, h) => h);
const label = (h: number) => `${String(h).padStart(2, '0')}:00`;

type Person = BreakSettings['people'][number];

/**
 * A configuração do intervalo — a aba "intervalo" das Configurações do legado:
 * a janela da base e quem tem qual horário de 15 minutos. Quem não tem horário
 * não tem intervalo.
 *
 * Duas leituras do mesmo formulário (`scope` na resposta): quem tem
 * `breaks.settings:editar` vê a janela e a base inteira; o líder de
 * departamento vê só a equipe dos setores que lidera, sem a janela. Gravar
 * mexe só em quem aparece na tela — o servidor não toca no resto.
 */
export function SettingsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const key = ['intervalo', 'configuracao', tenantId];

  const settings = useQuery({
    queryKey: key,
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(await api.breaks.settings(), 200, 'Não foi possível carregar a configuração.'),
  });

  const [start, setStart] = useState(15);
  const [end, setEnd] = useState(16);
  const [assigned, setAssigned] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!settings.data) return;
    setStart(settings.data.window.startHour);
    setEnd(settings.data.window.endHour);
    setAssigned(Object.fromEntries(settings.data.assignments.map((a) => [a.id, a.slot])));
  }, [settings.data]);

  const saved = (body: BreakSettings) => {
    client.setQueryData(key, body);
    void client.invalidateQueries({ queryKey: BREAK_CURRENT_KEY });
  };

  const saveWindow = useMutation({
    mutationFn: async () =>
      ok(
        await api.breaks.saveWindow({ body: { startHour: start, endHour: end } }),
        200,
        'Não foi possível gravar a janela.',
      ),
    onSuccess: saved,
  });
  const saveSlots = useMutation({
    mutationFn: async () =>
      ok(
        await api.breaks.saveSlots({
          body: {
            assignments: Object.entries(assigned)
              .filter(([, slot]) => slot !== '')
              .map(([userId, slot]) => ({ userId, slot })),
          },
        }),
        200,
        'Não foi possível gravar os horários.',
      ),
    onSuccess: saved,
  });

  /* Os horários da janela que está NA TELA — a pessoa vê o efeito antes de gravar. */
  const slots = breakSlotsOf({ startHour: start, endHour: end });
  const savedSlots = settings.data?.slots ?? [];
  const leaderView = settings.data?.scope === 'lider';
  const perSlot = useMemo(() => {
    const count: Record<string, number> = {};
    for (const slot of Object.values(assigned)) if (slot) count[slot] = (count[slot] ?? 0) + 1;
    return count;
  }, [assigned]);

  const columns: Column<Person>[] = [
    { title: 'Pessoa', cell: (p) => p.name, alignment: 'inicio' },
    { title: 'E-mail', cell: (p) => p.email, alignment: 'inicio' },
    {
      title: 'Horário',
      cell: (p) => {
        if (p.leader) {
          return <Badge tone="informacao">Líder — intervalo livre</Badge>;
        }
        const current = assigned[p.id] ?? '';
        const outside = current !== '' && !savedSlots.includes(current);
        return (
          <div className="flex items-center justify-center gap-2">
            <Select
              label={`Horário de ${p.name}`}
              hiddenLabel
              value={current}
              onChange={(e) => setAssigned((a) => ({ ...a, [p.id]: e.target.value }))}
            >
              <option value="">Sem horário</option>
              {outside && <option value={current}>{current} (fora da janela)</option>}
              {savedSlots.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
            {outside && <Badge tone="alerta">Fora da janela</Badge>}
          </div>
        );
      },
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader title="Configuração do intervalo">
        Cada pessoa com horário tira um intervalo de {BREAK_MINUTES} minutos por dia, dentro da
        janela. O horário vale para esta base.
      </PageHeader>

      {settings.isError && <Alert tone="erro">{settings.error.message}</Alert>}

      {leaderView ? (
        <Alert tone="informacao">
          Você vê a equipe dos departamentos que lidera. A janela é da base
          (das {label(start)} às {label(end)}), e quem lidera tira o intervalo livre: começa a
          qualquer hora dela em que caibam os {BREAK_MINUTES} minutos.
        </Alert>
      ) : (
      <Card
        title="Janela"
        descricao={`${
          settings.data?.isDefaultWindow
            ? 'Esta base usa a janela padrão, das 15h às 16h.'
            : 'O período do dia em que os intervalos acontecem, no horário de Fortaleza.'
        } Ela oferece ${slots.length} horário(s) de ${BREAK_MINUTES} minutos.`}
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-36">
            <Select label="Início" value={start} onChange={(e) => setStart(Number(e.target.value))}>
              {HOURS.slice(0, 24).map((h) => (
                <option key={h} value={h}>
                  {label(h)}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-36">
            <Select label="Fim" value={end} onChange={(e) => setEnd(Number(e.target.value))}>
              {HOURS.slice(1).map((h) => (
                <option key={h} value={h}>
                  {label(h)}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="contorno"
            loading={saveWindow.isPending}
            disabled={start >= end}
            onClick={() => saveWindow.mutate()}
          >
            Gravar janela
          </Button>
        </div>
        {start >= end && (
          <p className="mt-2 text-sm text-erro">O início precisa vir antes do fim.</p>
        )}
        {saveWindow.isError && <Alert tone="erro">{saveWindow.error.message}</Alert>}
      </Card>
      )}

      <Card
        title="Horários"
        descricao={
          savedSlots.map((s) => `${s}: ${perSlot[s] ?? 0}`).join(' · ') ||
          'A janela não oferece horário.'
        }
        actions={
          <Button loading={saveSlots.isPending} onClick={() => saveSlots.mutate()}>
            Gravar horários
          </Button>
        }
      >
        {saveSlots.isError && <Alert tone="erro">{saveSlots.error.message}</Alert>}
        <Table
          label="Pessoas e horários"
          columns={columns}
          items={settings.data?.people ?? []}
          rowKey={(p) => p.id}
          loading={settings.isLoading}
          empty={
            leaderView
              ? 'Ninguém nos departamentos que você lidera.'
              : 'Nenhuma pessoa com vínculo ativo nesta base.'
          }
        />
      </Card>
    </Page>
  );
}
